/*
  Окно «Отнести платёж к счёту»: выбор счёта человеком для спорного поступления.

  Аудит 28.10.2026, раздел 5.3. Когда система не предложила счёт, экран советовал «откройте
  карточку нужного счёта», но отнести платёж к счёту на карточке негде: платёж оставался
  неразнесённым. Сервер ручную привязку умеет (`POST /payments/{id}/match`); не хватало
  выбора счёта в кабинете.

  Счёт ищется по номеру серверным поиском реестра, как в самом реестре: весь портфель в
  браузер не загружается. Наугад не разносим (§3.10.2): кнопка включается, только когда
  человек выбрал строку из найденных, и в ней названы номер и адрес.
*/

import { useRef, useState } from 'react'

import { api } from '../api/client.js'
import { date, money } from '../api/format.js'
import { Field, Notice } from './Common.jsx'
import FormDialog from './entry/FormDialog.jsx'

const SHOWN = 10

export default function StatementAccountPick({ payment, onSaved, onClose }) {
  const [text, setText] = useState('')
  const [found, setFound] = useState(null)
  const [looking, setLooking] = useState(false)
  const [lookError, setLookError] = useState(null)
  const [account, setAccount] = useState(null)
  const latest = useRef(0)

  async function look() {
    const query = text.trim()
    if (!query) return
    const mine = ++latest.current
    setLooking(true)
    setLookError(null)
    setAccount(null)
    try {
      const result = await api.registry({ search: query, limit: SHOWN, offset: 0 })
      // Запоздавший ответ на прежний запрос новый не затирает.
      if (mine === latest.current) setFound(result)
    } catch (failure) {
      if (mine === latest.current) setLookError(failure)
    } finally {
      if (mine === latest.current) setLooking(false)
    }
  }

  const rows = found?.rows ?? []

  return (
    <FormDialog
      title="Отнести платёж к счёту"
      note={`${date(payment.paid_on)} · ${money(payment.amount)} · ${payment.payer ?? 'плательщик не указан'}`}
      submitLabel={account ? `Отнести к счёту № ${account.number}` : 'Отнести к счёту'}
      canSubmit={Boolean(account)}
      onSubmit={() => api.matchPayment(payment.payment_id, account.account_id)}
      onSaved={onSaved}
      onClose={onClose}
    >
      <div className="row">
        <Field label="Номер лицевого счёта" width="m">
          <input
            className="field__input"
            value={text}
            placeholder="номер или его часть"
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              // Enter ищет, а не отправляет окно: платёж без выбранного счёта разнести нельзя.
              if (event.key === 'Enter') {
                event.preventDefault()
                look()
              }
            }}
          />
        </Field>
        <button
          type="button"
          className="button button--quiet"
          disabled={looking || !text.trim()}
          onClick={look}
        >
          {looking ? 'Ищем…' : 'Найти'}
        </button>
      </div>
      <Notice error={lookError} />
      {found && rows.length === 0 ? (
        <div className="muted">Счетов с таким номером не нашлось. Проверьте номер.</div>
      ) : null}
      {rows.length > 0 ? (
        <div className="stack" role="group" aria-label="Найденные счета">
          {rows.map((row) => (
            <button
              key={row.account_id}
              type="button"
              className={`button${account?.account_id === row.account_id ? '' : ' button--quiet'}`}
              aria-pressed={account?.account_id === row.account_id}
              onClick={() => setAccount(row)}
            >
              № {row.number} · {row.address}
            </button>
          ))}
          {found.total > rows.length ? (
            <div className="muted">
              Показаны первые {rows.length} из {found.total}: уточните номер.
            </div>
          ) : null}
        </div>
      ) : null}
    </FormDialog>
  )
}
